import React, { useEffect, useState, useMemo } from 'react';
import { Card, Space, Typography, Collapse, Row, Col, Divider, Table, Tag, Button, Tooltip } from 'antd';
import moment from 'moment';
import { calculateImpressions } from '../../CampaignRequestor/CampaignRequestorUtil';
import { formatCurrency2SigFig, kmToFt } from '../../../../core/utils/campaigns';
import { audienceTypeMap, stateList } from '../../../../core/utils/constants/constants';
import { CampaignOrderQueueReviewAdGroup } from './CampaignOrderQueueReviewAdGroup';
import { CampaignOrderQueueReviewVideoAssets } from './CampaignOrderQueueReviewVideoAssets';
import { DownloadOutlined, CalendarOutlined, GiftOutlined, LockOutlined, RocketOutlined, CloudUploadOutlined, SyncOutlined, EditOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import { capitalize } from 'lodash';
import { CampaignOrderQueueReviewCampaignLaunchDetails } from './CampaignOrderQueueReviewCampaignLaunchDetails';
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { usePermify } from '@permify/react-role';

const { Panel } = Collapse;
const { Title, Text } = Typography;

export const CampaignOrderQueueReviewCampaign = ({
  lineItemIndex,
  campaignIndex,
  restField,
  form,
  dspType,
  setDspType,
  fieldName,
  onMediaStatusUpdate,
  isLaunching = false,
  isAdvancedMode = false,
}) => {
  const { currentCampaignOrder } = useCampaignRequestorContext();
  const { isRegulated, pricingModel } = currentCampaignOrder;
  const campaignType = currentCampaignOrder.campaigns[campaignIndex]?.type;
  const isVideoType = campaignType === 'VIDEO' || campaignType === 8;
  const lineItem = currentCampaignOrder.campaigns[campaignIndex].lineItems[lineItemIndex];
  const {
    name,
    startDate,
    endDate,
    budgetTotal,
    cpm,
    adGroup,
    firstPartyDataAudiences,
    audienceType,
    targetingType,
    targetingRegions,
    targetingLocations,
    targetingCountries,
    valueAdd,
    isOrigin,
    markedForDelivery,
    dspLineItemId,
  } = lineItem;

  // Check if this is a programmatic campaign and line item is already launched
  const campaign = currentCampaignOrder.campaigns[campaignIndex];
  const isProgrammaticCampaign = campaign?.isLaunchProgrammatically === true;
  const isLineItemLaunched = !!dspLineItemId;

  // Check if line item is ready for launch (programmatic campaign, not yet launched, marked for delivery)
  const isReadyForLaunch = isProgrammaticCampaign && !isLineItemLaunched && markedForDelivery;

  // Detect if line item has pending changes (status-based detection)
  const isLineItemModified = useMemo(() => {
    // Only show MODIFIED badge for programmatic campaigns with launched line items
    if (!isProgrammaticCampaign || !isLineItemLaunched) return false;

    // Check if status is PENDING (indicates changes waiting to be applied)
    const status = lineItem?.status;
    return status === 'PLAY_PENDING' || status === 'PAUSED_PENDING';
  }, [isProgrammaticCampaign, isLineItemLaunched, lineItem.status]);

  // Permission checking for Flight Schedules visibility
  const { isAuthorized } = usePermify();
  const [showFlightSchedules, setShowFlightSchedules] = useState(false);

  // Check if user can see Flight Schedules
  useEffect(() => {
    const checkPermissions = async () => {
      const hasAdOpsPermission = await isAuthorized(['isTrafficker', 'isProcessor'], []);

      // Flight Schedules: Hide if DRAFT status, otherwise show only to Ad Ops
      if (currentCampaignOrder.status === 'DRAFT') {
        setShowFlightSchedules(false);
      } else {
        setShowFlightSchedules(hasAdOpsPermission);
      }
    };

    checkPermissions();
  }, [currentCampaignOrder.status, isAuthorized]);

  const getStateCode = (state, stateList) => {
    const stateEntry = stateList.find((val) => state === val.State);
    return stateEntry ? stateEntry.Code : '';
  };

  const processGeoTargets = (geoTarget, stateList) => {
    return geoTarget.circles.map((circle) => {
      const location = circle.location || geoTarget.location || {};
      const state = location.state ? capitalize(location.state) : '';
      const stateCode = getStateCode(state, stateList);

      return {
        Name: location.name || '',
        Type: 'CIRCLE',
        Address: location.formattedAddress || '',
        Street: location.street || '',
        City: location.city || '',
        County: location.county || '',
        State: stateCode || location.state || '',
        Country: location.country || '',
        Zip: location.zip || '',
        Latitude: circle.coordinate.lat,
        Longitude: circle.coordinate.lng,
        'Radius (Miles)': Math.round(circle.radius * 1000),
      };
    });
  };

  const flattenLocations = (targetingLocations, stateList) => {
    return targetingLocations.flatMap((targetingLocation) =>
      targetingLocation.geoTargets.flatMap((geoTarget) => processGeoTargets(geoTarget, stateList))
    );
  };

  const flattenRegions = (targetingRegions) => {
    return targetingRegions.flatMap((regionGroup) =>
      regionGroup.regions.map((region) => ({
        'Region Group Name': regionGroup.name,
        'Region': region,
      }))
    );
  };

  const csvData = flattenLocations(targetingLocations, stateList);
  const regionCsvData = flattenRegions(targetingRegions || []);

  return (
    <div id={`campaign-${campaignIndex}-lineitem-${lineItemIndex}`}>
      <Collapse bordered={false} defaultActiveKey={[0]}>
        <Panel open={true} header={
          <Space size="small" align="center">
            <Title level={5} style={{ margin: 0 }}>LINE ITEM {lineItemIndex + 1}</Title>
            {!lineItem.id && (
              <Tooltip title="New line item (not yet saved)">
                <Tag icon={<PlusCircleOutlined />} color="cyan">
                  New
                </Tag>
              </Tooltip>
            )}
            {valueAdd && (
              <Tooltip title="Value Add">
                <Tag icon={<GiftOutlined />} color="purple">
                  Value Add
                </Tag>
              </Tooltip>
            )}
            {isOrigin && (
              <Tooltip title="Origin (Client Created)">
                <Tag icon={<LockOutlined />} color="blue">
                  Origin
                </Tag>
              </Tooltip>
            )}
            {markedForDelivery && (
              <Tooltip title="Marked for Delivery">
                <Tag icon={<RocketOutlined />} color="green">
                  Delivery
                </Tag>
              </Tooltip>
            )}
            {isReadyForLaunch && (
              <Tooltip title="New line item ready for programmatic launch">
                <Tag icon={<SyncOutlined />} color="orange">
                  Ready for Launch
                </Tag>
              </Tooltip>
            )}
            {isProgrammaticCampaign && isLineItemLaunched && (
              <Tooltip title="Line item has been launched to the DSP">
                <Tag icon={<CloudUploadOutlined />} color="purple">
                  Launched to DSP
                </Tag>
              </Tooltip>
            )}
            {isLineItemModified && isProgrammaticCampaign && isLineItemLaunched && (
              <Tooltip title="Line item has been modified and is ready to be updated when re-launched">
                <Tag icon={<EditOutlined />} color="orange">
                  Ready for Update
                </Tag>
              </Tooltip>
            )}
          </Space>
        }>
          <Card>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* LAUNCH DETAILS - Priority Section (Only visible in Advanced Mode) */}
              {isAdvancedMode && !['PENDING', 'LIVE_PENDING', 'PAUSED_PENDING'].includes(currentCampaignOrder.status) && (
                <>
                  <CampaignOrderQueueReviewCampaignLaunchDetails
                    form={form}
                    fieldName={fieldName}
                    campaignIndex={campaignIndex}
                    lineItemIndex={lineItemIndex}
                    dspType={dspType}
                    setDspType={setDspType}
                    isLaunching={isLaunching}
                  />
                </>
              )}

              <Collapse
                bordered={false}
                defaultActiveKey={
                  ['PENDING', 'LIVE_PENDING', 'PAUSED_PENDING'].includes(currentCampaignOrder.status)
                    ? ['line-item-details']
                    : []
                }
              >
                <Panel key="line-item-details" header={<Title level={5}>LINE ITEM DETAILS</Title>}>
                  <Card>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      {/* LINE ITEM DETAILS */}
                      <Row gutter={20}>
                        <Col span={4}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text type="secondary">Line Item Name</Text>
                            <Text>{name}</Text>
                          </Space>
                        </Col>
                        <Col span={4}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text type="secondary">
                              {isVideoType ? 'Creative Groups' : 'Ad Groups'}
                            </Text>
                            <Text>
                              {adGroup &&
                                adGroup.adGroupCreatives &&
                                adGroup.adGroupCreatives.length}
                            </Text>
                          </Space>
                        </Col>
                        <Col span={4}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text type="secondary">
                              {isVideoType ? 'Video Assets' : 'Ad Units'}
                            </Text>
                            <Text>
                              {adGroup &&
                                adGroup.adGroupCreatives &&
                                adGroup.adGroupCreatives.reduce((acc, curr) => {
                                  return acc + curr.medias.length;
                                }, 0)}
                            </Text>
                          </Space>
                        </Col>
                        <Col span={4}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text type="secondary">Start Date</Text>
                            <Text>{moment.utc(startDate).format('MM/DD/YYYY')}</Text>
                          </Space>
                        </Col>
                        <Col span={4}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text type="secondary">End Date</Text>
                            <Text>{moment.utc(endDate).format('MM/DD/YYYY')}</Text>
                          </Space>
                        </Col>
                      </Row>
                      <Divider plain></Divider>
                      <Row gutter={20}>
                        {pricingModel !== 'Percentage' && (
                          <Col span={4}>
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                              <Text type="secondary">Wholesale CPM</Text>
                              <Text></Text>
                            </Space>
                          </Col>
                        )}
                        {pricingModel !== 'Percentage' && (
                          <Col span={4}>
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                              <Text type="secondary">CPM</Text>
                              <Text>{cpm}</Text>
                            </Space>
                          </Col>
                        )}
                        {pricingModel !== 'Percentage' && (
                          <Col span={4}>
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                              <Text type="secondary">Impressions</Text>
                              <Text>
                                {calculateImpressions({
                                  budget: budgetTotal,
                                  cpm,
                                }).toLocaleString()}
                              </Text>
                            </Space>
                          </Col>
                        )}
                        <Col span={4}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text type="secondary">Line Item Budget</Text>
                            <Text>{formatCurrency2SigFig(budgetTotal)}</Text>
                          </Space>
                        </Col>
                        <Col span={4}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text type="secondary">End Date</Text>
                            <Text>{moment.utc(endDate).format('MM/DD/YYYY')}</Text>
                          </Space>
                        </Col>
                      </Row>

                      {/* Flight Schedules Section - View Only - Hidden if DRAFT or user lacks permissions */}
                      {showFlightSchedules && lineItem.flightSchedules && lineItem.flightSchedules.length > 0 && (
                        <>
                          <Divider plain></Divider>
                          <Row gutter={20}>
                            <Col span={24}>
                              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                                  <CalendarOutlined style={{ marginRight: 8 }} />
                                  FLIGHT SCHEDULES
                                </Text>
                                <Table
                                  dataSource={lineItem.flightSchedules.map((flight, idx) => ({
                                    ...flight,
                                    key: flight.id || idx,
                                  }))}
                                  columns={[
                                    {
                                      title: 'Flight',
                                      key: 'flight',
                                      width: 80,
                                      render: (_, __, idx) => (
                                        <Tag color="blue">Flight {idx + 1}</Tag>
                                      ),
                                    },
                                    {
                                      title: 'Start Date',
                                      dataIndex: 'startDate',
                                      key: 'startDate',
                                      render: (date) => date ? moment.utc(date).format('MM/DD/YYYY') : '-',
                                    },
                                    {
                                      title: 'End Date',
                                      dataIndex: 'endDate',
                                      key: 'endDate',
                                      render: (date) => date ? moment.utc(date).format('MM/DD/YYYY') : '-',
                                    },
                                    {
                                      title: 'Budget',
                                      dataIndex: 'budget',
                                      key: 'budget',
                                      render: (budget) => budget ? formatCurrency2SigFig(budget) : '-',
                                    },
                                    ...(pricingModel !== 'Percentage' ? [{
                                      title: 'CPM',
                                      dataIndex: 'cpm',
                                      key: 'cpm',
                                      render: (cpm) => cpm ? `$${cpm}` : '-',
                                    }] : []),
                                  ]}
                                  pagination={false}
                                  size="small"
                                  style={{ marginTop: 8 }}
                                />
                                <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
                                  Total: {lineItem.flightSchedules.length} flight schedule(s)
                                </div>
                              </Space>
                            </Col>
                          </Row>
                        </>
                      )}

                      {/* AUDIENCES */}
                      {/* DISPLAY AUDIENCE DEPENDING AUDIENCE TYPE  */}
                      {audienceType === 'FIRST_PARTY_DATA' && (
                        <div className="requester-table-wrapper" style={{ marginTop: 20 }}>
                          <>
                            <div className="requester-table-header">
                              <Row>
                                <Col span={12}>
                                  <Title level={5}>FIRST PARTY AUDIENCES</Title>
                                </Col>
                              </Row>
                            </div>
                            {!firstPartyDataAudiences.length ? (
                              <div style={{ padding: 20 }}>
                                <Space
                                  direction="vertical"
                                  size="small"
                                  align="center"
                                  style={{ width: '100%' }}
                                ></Space>
                              </div>
                            ) : (
                              <Table
                                rowKey="id"
                                columns={[
                                  {
                                    title: 'Audience Name',
                                    dataIndex: 'name',
                                    key: 'name',
                                    ellipsis: true, // Enables ellipsis for text overflow
                                    width: 200, // Set a fixed width for the column
                                  },
                                  {
                                    title: 'Devices',
                                    dataIndex: 'deviceCount',
                                    key: 'deviceCount',
                                    render: (count) => (count ? count.toLocaleString() : 'N/A'),
                                  },
                                  {
                                    title: 'Match %',
                                    dataIndex: 'matchRate',
                                    key: 'matchRate',
                                    render: (rate) =>
                                      rate ? rate.toFixed(2).toLocaleString() : 'N/A',
                                  },
                                  {
                                    title: 'Status',
                                    dataIndex: 'activated',
                                    key: 'activated',
                                    render: (activated) =>
                                      activated ? 'Activated' : 'Deactivated',
                                  },

                                  {
                                    title: 'Uploaded',
                                    dataIndex: 'createdAt',
                                    key: 'createdAt',
                                    render: (date) => moment.utc(date).format('MM/DD/YYYY hh:mma'),
                                  },
                                ]}
                                dataSource={firstPartyDataAudiences}
                                pagination={false}
                                className="requester-table"
                                summary={() => (
                                  <Table.Summary.Row>
                                    <Table.Summary.Cell index={0}>
                                      {firstPartyDataAudiences.length} Audiences
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2}>
                                      {firstPartyDataAudiences
                                        .reduce((acc, audience) => acc + audience.deviceCount, 0)
                                        .toLocaleString()}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell colSpan={3} index={3}>
                                      {firstPartyDataAudiences
                                        .reduce((acc, audience) => acc + audience.matchRate, 0)
                                        .toFixed(2)
                                        .toLocaleString()}{' '}
                                      %
                                    </Table.Summary.Cell>
                                  </Table.Summary.Row>
                                )}
                              />
                            )}
                          </>
                        </div>
                      )}
                      {audienceType === 'ALL_USERS' && (
                        <>
                          <Divider plain></Divider>
                          <Row gutter={20}>
                            <Col span={12}>
                              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text type="secondary">Audience Type</Text>
                                <Text>
                                  {audienceTypeMap[audienceType]} {isRegulated ? 'aged 21+' : ''}
                                </Text>
                              </Space>
                            </Col>
                          </Row>
                        </>
                      )}
                      {/* TARGETING */}
                      {/* TARGETING REGION TABLE */}
                      {targetingType === 'REGION' && (
                        <div className="requester-table-wrapper" style={{ marginTop: 20 }}>
                          <div className="requester-table-header">
                            <Row>
                              <Col span={12}>
                                <Title level={5}>TARGETING: REGION GROUPS</Title>
                              </Col>
                            </Row>
                          </div>
                          {targetingRegions && targetingRegions.length ? (
                            <Table
                              rowKey="id"
                              columns={[
                                {
                                  title: 'Region Group Name',
                                  dataIndex: 'name',
                                  key: 'name',
                                },
                                {
                                  title: 'Regions',
                                  dataIndex: 'regions',
                                  key: 'regions',
                                  render: (regions) =>
                                    regions.map((region) => (
                                      <Tag color="green" key={region}>
                                        {region}
                                      </Tag>
                                    )),
                                },
                              ]}
                              dataSource={targetingRegions}
                              pagination={false}
                              className="requester-table"
                              summary={() =>
                                targetingRegions && targetingRegions.length ? (
                                  <Table.Summary.Row>
                                    <Table.Summary.Cell index={0}>
                                      {targetingRegions.length} Region Groups
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1}>
                                      {targetingRegions.reduce(
                                        (acc, regionGroup) => acc + regionGroup.regions.length,
                                        0
                                      )}{' '}
                                      Individual Regions
                                    </Table.Summary.Cell>
                                  </Table.Summary.Row>
                                ) : (
                                  <></>
                                )
                              }
                            />
                          ) : (
                            <></>
                          )}
                        </div>
                      )}
                      {/* TARGETING LOCAL TABLE */}
                      {targetingType === 'LOCAL' && (
                        <div className="requester-table-wrapper" style={{ marginTop: 20 }}>
                          <div className="requester-table-header">
                            <Row>
                              <Col span={12}>
                                <Title level={5}>TARGETING: LOCAL</Title>
                              </Col>
                              <Col span={12}>
                                <div
                                  style={{
                                    display: 'flex',
                                    gap: 8,
                                    justifyContent: 'flex-end',
                                  }}
                                >
                                  <Button>
                                    <CSVLink
                                      filename={`${lineItem.name}-Geo.csv`}
                                      style={{ textDecoration: 'none' }}
                                      data={csvData}
                                    >
                                      <DownloadOutlined />
                                      Download CSV
                                    </CSVLink>
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          </div>
                          {targetingLocations && targetingLocations.length ? (
                            <Table
                              rowKey="id"
                              columns={[
                                {
                                  title: 'Targeting Group Name',
                                  dataIndex: 'name',
                                  key: 'name',
                                },
                                {
                                  title: 'Locations',
                                  dataIndex: 'locations',
                                  key: 'locations',
                                  width: '40%',
                                  render: (_, { geoTargets }) => (
                                    <div>
                                      {geoTargets &&
                                        geoTargets.map((target) => {
                                          return target.location ? (
                                            <Tag color="blue" key={target.id}>
                                              {target.location.name}
                                            </Tag>
                                          ) : (
                                            <></>
                                          );
                                        })}
                                    </div>
                                  ),
                                },
                                {
                                  title: 'Created On',
                                  dataIndex: 'createdAt',
                                  key: 'createdAt',
                                  render: (text) => moment.utc(text).format('MM/DD/YYYY hh:mma'),
                                },
                              ]}
                              dataSource={targetingLocations}
                              pagination={false}
                              className="requester-table"
                              summary={() =>
                                targetingLocations && targetingLocations.length ? (
                                  <Table.Summary.Row>
                                    <Table.Summary.Cell index={0}>
                                      {targetingLocations.length} Targeting Groups
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1}>
                                      {targetingLocations.reduce(
                                        (acc, targetingGroup) =>
                                          acc + targetingGroup.geoTargets.length,
                                        0
                                      )}{' '}
                                      Targeting Locations
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2}></Table.Summary.Cell>
                                  </Table.Summary.Row>
                                ) : (
                                  <></>
                                )
                              }
                            />
                          ) : (
                            <></>
                          )}
                        </div>
                      )}
                      {targetingType === 'COUNTRY' && (
                        <>
                          <Divider plain></Divider>
                          <Row gutter={20}>
                            <Col span={12}>
                              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text type="secondary">Targeting Type</Text>
                                <Text>
                                  {targetingType}:{' '}
                                  {targetingCountries.map((country) => country.name).join(', ')}
                                </Text>
                              </Space>
                            </Col>
                          </Row>
                        </>
                      )}
                      {/* CREATIVE ASSETS */}
                      {adGroup && adGroup.adGroupCreatives.length ? (
                        isVideoType ? (
                          <CampaignOrderQueueReviewVideoAssets
                            adGroup={adGroup}
                            campaignIndex={campaignIndex}
                            lineItemIndex={lineItemIndex}
                            onMediaStatusUpdate={onMediaStatusUpdate}
                          />
                        ) : (
                          adGroup.adGroupCreatives.map((adGroupCreative, adGroupCreativeIndex) => (
                            <CampaignOrderQueueReviewAdGroup
                              key={adGroupCreativeIndex}
                              adGroup={adGroup}
                              adGroupCreative={adGroupCreative}
                              adGroupCreativeIndex={adGroupCreativeIndex}
                              campaignIndex={campaignIndex}
                              lineItemIndex={lineItemIndex}
                              onMediaStatusUpdate={onMediaStatusUpdate}
                            />
                          ))
                        )
                      ) : (
                        <></>
                      )}
                    </Space>
                  </Card>
                </Panel>
              </Collapse>
            </Space>
          </Card>
        </Panel>
      </Collapse>
    </div>
  );
};
